import { Button } from '@mui/material'
import { usePharmacyMedications } from 'data/treatments'
import { CircularProgress } from '@mui/material'
import { Outlet, useMatch, useNavigate, useParams } from 'react-router-dom'
import { formatCurrency, formatDate } from 'utils'
import ErrorFallback from 'components/ErrorFallback'
import EmptyState from 'components/EmptyState'
import { useState } from 'react'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import Filter from './Filter'
import { Medication } from 'types/treatments'

export default function PharmacyMedications() {
  const isPage = !!useMatch('/providers/pharmacies/:id/medications/medications')
  const { id } = useParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Medication['medication_status'] | undefined>()
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  })
  const { data, isLoading, isError, refetch } = usePharmacyMedications({
    offset: paginationModel.page,
    limit: paginationModel.pageSize,
    pharmacyId: id as string,
    medication_status: status,
  })

  return (
    <>
      {isPage ? (
        <div className="lg:w-[39rem] mx-auto grid gap-4">
          <div className="grid place-items-end">
            <Filter
              filterByStatus={(data) => {
                setStatus(data as Medication['medication_status'])
              }}
              status={status}
              key={`${status}`}
            />
          </div>
          {isLoading ? (
            <>
              <div className="grid place-items-center mt-16 h-96">
                <CircularProgress />
              </div>
            </>
          ) : isError ? (
            <ErrorFallback
              message="Something went wrong"
              description="We could not fetch medications"
              reset={refetch}
              buttonText="Try again"
            />
          ) : data?.item_count === 0 ? (
            <EmptyState text="There are no medication yet" />
          ) : (
            <div className="grid gap-4">
              {data?.items.map((treatment) => {
                return (
                  <div
                    key={treatment.id}
                    className="rounded-xl p-5 border border-default font-medium space-y-4 w-full"
                  >
                    <div className="flex justify-between">
                      <p className="font-medium">
                        <span className="font-semibold"> Date </span> {' : '}
                        {formatDate(treatment?.created_at)}
                      </p>

                      {/* <Button
                        className="hover:bg-transparent py-0 h-fit"
                        variant="text"
                        onClick={() => navigate(`${treatment.id}/refill-history`)}
                      >
                        Refill History
                      </Button> */}
                    </div>
                    <p className="font-medium">
                      <span className="font-semibold"> Drug name </span> {' : '}
                      {treatment?.metadata?.drug_name}
                    </p>
                    <p className="font-medium">
                      <span className="font-semibold">Dose</span> {' : '} {treatment?.dose}
                    </p>
                    <p className="font-medium">
                      <span className="font-semibold">Dosage form</span> {' : '}{' '}
                      {treatment?.dosage_form}
                    </p>
                    <p className="font-medium">
                      <span className="font-semibold">Prescribed By </span> {' : '}{' '}
                      {treatment?.metadata?.staff_name}
                    </p>
                    <p className="font-medium">
                      <span className="font-semibold">Dispensed By </span> {' : '}{' '}
                      {treatment?.metadata?.staff_name}
                    </p>

                    <p>
                      <span className="font-semibold">Location</span> {' : '}{' '}
                      {treatment?.metadata?.location_name}
                    </p>

                    <p>
                      <span className="font-semibold"> Price</span> {' : '}
                      {formatCurrency(Number(treatment?.price))}
                    </p>

                    <div className="justify-between font-medium flex items-center">
                      <p>
                        <span className="font-semibold">Approved Quantity</span> {' : '}{' '}
                        {treatment?.dosage.quantity}
                      </p>{' '}
                      <Button onClick={() => navigate(treatment.id)}>View Dosage</Button>
                    </div>
                  </div>
                )
              })}

              {data && (
                <div className="justify-between text-neutral-500 mt-20 flex">
                  <Button
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    onClick={() => {
                      setPaginationModel({
                        ...paginationModel,
                        page: paginationModel.page - 1,
                      })
                    }}
                    disabled={paginationModel.page + 1 === 1}
                    className="px-3 border border-default text-neutral-500"
                  >
                    Previous
                  </Button>
                  <div className="page-number">
                    Page {paginationModel.page + 1} of{' '}
                    {Math.ceil(data?.item_count / paginationModel.pageSize)}
                  </div>
                  <Button
                    startIcon={<ArrowForwardIcon className="text-error-900" />}
                    variant="outlined"
                    onClick={() => {
                      setPaginationModel({
                        ...paginationModel,
                        page: paginationModel.page + 1,
                      })
                    }}
                    disabled={
                      paginationModel.page + 1 ===
                      Math.ceil(data?.item_count / paginationModel.pageSize)
                    }
                    className="px-3 py-1 border border-error-600 text-error-900"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <Outlet />
      )}
    </>
  )
}
